import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Events } from "../../pages/marketing/Event/Events";
import { Spark } from "../../pages/marketing/Spark/Spark";
import { Matches } from "../../pages/marketing/Matches/Matches";
import { useStoreData } from "../../lib/store/store";
import { isLoggedIn } from "../../lib/isLoggedIn";
import { background } from "../../lib/useBackground";

type TabKey = "events" | "spark" | "matches";

// Backgrounds are a property of the active tab, not the mounted component —
// with all three tabs kept alive simultaneously we apply the background here
// so switching tabs updates it cleanly. Empty string = use the page default.
const TAB_BACKGROUNDS: Record<TabKey, string> = {
  events:
    "linear-gradient(to bottom, var(--chakra-colors-bg-brand) 50%, var(--chakra-colors-bg-canvas) 50%) no-repeat fixed",
  spark: "",
  matches: "",
};

function activeTabForPath(pathname: string): TabKey | undefined {
  const p = pathname.toLowerCase();
  if (p === "/spark") return "spark";
  if (p === "/matches" || p.startsWith("/matches/")) return "matches";
  // /settings/* overlays on top of whichever tab the user was on — don't
  // switch the visible tab underneath while the modal animates in.
  if (p === "/settings" || p.startsWith("/settings/")) return undefined;
  return "events";
}

// Schedule a callback for when the browser is next idle. Falls back to
// setTimeout on Safari / older WebKit where requestIdleCallback isn't defined.
function scheduleIdle(cb: () => void): () => void {
  const ric = (
    window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    }
  );
  if (typeof ric.requestIdleCallback === "function") {
    const id = ric.requestIdleCallback(cb, { timeout: 2000 });
    return () => ric.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(cb, 300);
  return () => window.clearTimeout(id);
}

/**
 * Keeps the three bottom-tab pages mounted so switching between them doesn't
 * retrigger their mount-time network requests. Only the active tab is mounted
 * on first visit; the other two are mounted lazily after the first paint
 * settles, so initial navigation cost matches the pre-keep-alive behavior.
 *
 * Auth-gated tabs (Spark, Matches) only mount once the user is logged in —
 * otherwise their mount effects would fire failed requests or redirect loops.
 */
export function KeepAliveTabs() {
  const { pathname } = useLocation();
  const storeData = useStoreData();
  const loggedIn = useMemo(() => isLoggedIn(storeData), [storeData]);
  // When the path doesn't identify a tab (e.g. /settings/*, which overlays
  // on top), keep showing whichever tab was active before the overlay opened.
  const [active, setActive] = useState<TabKey>(
    () => activeTabForPath(pathname) ?? "events"
  );
  useEffect(() => {
    const next = activeTabForPath(pathname);
    if (next) setActive(next);
  }, [pathname]);

  const [mounted, setMounted] = useState<Record<TabKey, boolean>>(() => ({
    events: active === "events",
    spark: active === "spark",
    matches: active === "matches",
  }));

  // Make sure whichever tab becomes active is mounted immediately, even if
  // the user navigated to it before the idle callback fired.
  useEffect(() => {
    setMounted((prev) => (prev[active] ? prev : { ...prev, [active]: true }));
  }, [active]);

  // Apply the active tab's background via the shared Background singleton that
  // Page.tsx listens on. Clears on unmount so leaving the tab cluster (e.g.
  // to Profile) falls back to the page default.
  useEffect(() => {
    const value = TAB_BACKGROUNDS[active];
    background.val = value;
    background.dispatchEvent(
      new CustomEvent<{ value: string }>("update", { detail: { value } })
    );
    return () => {
      background.val = "";
      background.dispatchEvent(
        new CustomEvent<{ value: string }>("update", { detail: { value: "" } })
      );
    };
  }, [active]);

  // Once all three are mounted there's nothing left to schedule.
  const allMounted = mounted.events && mounted.spark && mounted.matches;

  useEffect(() => {
    if (allMounted) return;
    if (!loggedIn) return;
    const cancel = scheduleIdle(() => {
      setMounted({ events: true, spark: true, matches: true });
    });
    return cancel;
  }, [allMounted, loggedIn]);

  return (
    <>
      {mounted.events && (
        <div style={{ display: active === "events" ? "contents" : "none" }}>
          <Events />
        </div>
      )}
      {mounted.spark && (
        <div style={{ display: active === "spark" ? "contents" : "none" }}>
          <Spark />
        </div>
      )}
      {mounted.matches && (
        <div style={{ display: active === "matches" ? "contents" : "none" }}>
          <Matches />
        </div>
      )}
    </>
  );
}
